import { clampWindowStart, MAX_ENUMERATED_DATES } from '~utils/enumerate-dates';
import type {
    KpiCardData,
    KpiCardDataRequest,
    KpiCardState,
    KpiTrend,
} from '~containers/analytics/kpis/types';
import type {
    DegradationCause,
    EnvelopeCaveat,
    KpiEnvelope,
} from '~stores/kpi-envelope';

/**
 * Pure composition for the fan-out `app-downloads` source: no network, no
 * store, no clock. It normalizes each per-platform envelope
 * (`GET /analytics/downloads/{ios,android}`) into one shared shape, then merges
 * the pair into the canonical `KpiCardData` for the requested form.
 *
 * Splitting it out from the source keeps the interesting rules (what a missing
 * platform does to each form, how the two donuts are rebuilt) testable on
 * fixtures instead of behind two mocked fetches.
 *
 * It sits in `helpers/` rather than next to the source because it has two
 * consumers in two layers: the KPI card source, and the Onboarding store's
 * acquisition-funnel block. Only `composeDownloadsCardData` knows about cards;
 * the store reads `normalizePlatformDownloads`, so the funnel and the cards
 * degrade on the same rules and say the same sentences.
 */

/** The two platform endpoints the source fans out to. */
export type DownloadsPlatform = 'ios' | 'android';

/** How each platform is named to the reader, in donut slices and in every
 * degradation reason. `iOS`/`Android` are also the labels the retired combined
 * envelope emitted for its `device` breakdown rows, so the by-platform donut
 * keeps its brand colors (see `DEVICE_DONUT_COLORS`). */
export const DOWNLOADS_PLATFORM_LABELS: Record<DownloadsPlatform, string> = {
    ios: 'iOS',
    android: 'Android',
};

/** Order every combined output follows, matching the retired `device`
 * breakdown's row order so the by-platform donut is unchanged. */
export const DOWNLOADS_PLATFORMS: DownloadsPlatform[] = ['ios', 'android'];

/** The only metric either envelope serves: that platform's period total. */
export const DOWNLOADS_METRIC = 'total';

/** One row of an envelope's `brand` breakdown (Couche-Tard / Circle K). */
export interface DownloadsBrandRow {
    key: string;
    label: string;
    value: number;
}

/**
 * One platform's response, normalized.
 *
 * - `served`: the endpoint answered with a readable total.
 * - `unavailable`: the endpoint answered honestly that it has nothing for this
 *   window (a store outage, a window past the platform's freshness date, a
 *   province selection off the CT/CK boundary). Its `reason` and `cause` are
 *   the API's own.
 * - `failed`: no usable envelope at all (the request failed, or the payload was
 *   missing its `total`). The reason is ours, since there is no API one; the
 *   cause is pinned to `source_unavailable` for the same reason.
 */
export type NormalizedPlatformDownloads =
    | {
          status: 'served';
          /** Period total for this platform. */
          total: number;
          /** The compare-period total, only when the endpoint served one. The
           * API drops the compare whenever the compare window is not fully
           * covered by the platform's data, so this is legitimately absent on a
           * card that still has a fresh total. */
          compare?: number;
          /** `brand` rows, or `undefined` when the envelope emitted no
           * usable such axis: no axis at all, or one whose rows do not all
           * report the metric (see `reportsMetric`). The distinction matters:
           * an unusable axis is a contract violation (an error tile), an empty
           * axis is data. */
          brandRows?: DownloadsBrandRow[];
          /** Per-day points, or `undefined` when the envelope emitted no
           * `series` axis at all (the usual case under `granularity=total`, a
           * contract violation under `daily`). The same distinction `brandRows`
           * draws: no axis is a platform with no line to draw, an empty axis is
           * a platform with nothing to show. Truncated at this platform's
           * freshness date, so it can be shorter than the requested window, and
           * stripped of any day the platform did not report (see
           * `reportsMetric`). Consumed by the `downloads` chart form. */
          series?: { date: string; value: number }[];
          /** How far this platform's source is complete, in the envelope's own
           * words (`min(range.endDate, platform freshness)`). The declared
           * freshness, not the freshness the points imply: a platform that
           * reported no rows at all still declares one, and that is the only
           * thing separating "complete through the 14th, nothing to show" from
           * "said nothing". Absent when the envelope declared none. */
          dataCompleteThrough?: string;
          /** Envelope-level notes carried verbatim (a single-brand region
           * approximation, a truncated store window). Merged across both
           * platforms and deduped by the caller. */
          caveats?: EnvelopeCaveat[];
      }
    | { status: 'unavailable'; reason: string; cause?: DegradationCause }
    | { status: 'failed'; reason: string; cause: 'source_unavailable' };

/** A platform that answered with data. */
type ServedDownloads = Extract<
    NormalizedPlatformDownloads,
    { status: 'served' }
>;

/** A platform that did not: either way it carries a reason for the reader. */
type MissingDownloads = Exclude<
    NormalizedPlatformDownloads,
    { status: 'served' }
>;

/** One platform kept next to its own response, so no output labels a figure by
 * matching array positions. */
interface PlatformEntry {
    platform: DownloadsPlatform;
    data: NormalizedPlatformDownloads;
}

/** An entry whose platform answered. */
type ServedEntry = PlatformEntry & { data: ServedDownloads };

const isServedEntry = (entry: PlatformEntry): entry is ServedEntry =>
    entry.data.status === 'served';

/**
 * Read a REPORTED metric cell as a finite number. What is left once
 * `reportsMetric` has run is a `number` or a `string`, and a string that does
 * not parse (`'N/A'`, `''`) reads as 0 rather than `NaN`, which keeps one
 * unreadable cell from collapsing a whole donut to 0%.
 *
 * It must never be handed a cell the platform did not report, which on this
 * contract means absent OR `null`. This is the one card where the lenient
 * reading cannot be tolerated: its whole premise is that a reported 0 is data
 * and an unreported day is not, so either of those coerced to 0 would draw a
 * real zero over a gap. Callers check `reportsMetric` first and act on the
 * absence themselves.
 */
const finiteCell = (cell: unknown): number => {
    const n = Number(cell ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Whether a series point or a breakdown row actually REPORTS the metric, which
 * takes two runtime facts the type cannot express.
 *
 * Absent: the envelope schema reads both through a `catchall` and drops every
 * cell it cannot model, then types what survives as `Record<string,
 * MetricCell>`. The type says the key is always there; a dropped cell says
 * otherwise.
 *
 * `null`: contractually a cell is `number | string | null`, so `null` is not a
 * serialization accident but a value the API is allowed to send, and its
 * meaning is suppressed / not measurable (API-26), never zero. It says exactly
 * what an absent cell says, so it takes exactly the same path.
 *
 * A string that does not parse is NOT this case: the platform did report the
 * day, the cell is just unreadable, and `finiteCell` renders it 0.
 */
const reportsMetric = (cells: Record<string, unknown>): boolean => {
    const { [DOWNLOADS_METRIC]: cell } = cells;
    return cell !== undefined && cell !== null;
};

/** Normalize one platform's envelope (or the absence of one). */
export const normalizePlatformDownloads = (
    platform: DownloadsPlatform,
    envelope: KpiEnvelope | null
): NormalizedPlatformDownloads => {
    const label = DOWNLOADS_PLATFORM_LABELS[platform];
    if (!envelope) {
        return {
            status: 'failed',
            reason: `${label} downloads could not be loaded.`,
            cause: 'source_unavailable',
        };
    }
    // Checked before the metric: a degraded envelope legitimately carries no
    // metrics at all, and its reason is far better than ours.
    if (envelope.degradation) {
        return {
            status: 'unavailable',
            reason: envelope.degradation.reason,
            ...(envelope.degradation.cause !== undefined && {
                cause: envelope.degradation.cause,
            }),
        };
    }
    const total = envelope.metrics[DOWNLOADS_METRIC];
    // A declared-unavailable total (a null scalar value) takes the same path
    // as a missing/non-scalar one: there is no per-row granularity here to
    // render partially, only a single period total.
    if (total?.type !== 'scalar' || total.value == null) {
        return {
            status: 'failed',
            reason: `${label} downloads could not be loaded.`,
            cause: 'source_unavailable',
        };
    }
    const brand = envelope.breakdowns.find((b) => b.by === 'brand');
    // A row that does not report the metric (absent cell or `null`) makes the
    // whole axis unusable, the same way an absent axis does: a donut slice
    // silently sized 0 misstates every other slice's proportion, so this
    // reaches the reader as an error tile instead (see `brandRows`).
    const brandRows =
        brand !== undefined && brand.rows.every(reportsMetric)
            ? brand.rows.map((row) => ({
                  key: row.key,
                  label: row.label,
                  value: finiteCell(row[DOWNLOADS_METRIC]),
              }))
            : undefined;
    return {
        status: 'served',
        total: total.value,
        ...(total.compare !== undefined && { compare: total.compare.value }),
        ...(brandRows !== undefined && { brandRows }),
        // A day the platform did not report, whether its cell is absent or
        // `null`, is dropped rather than zeroed, so it falls into the gap the
        // renderer already draws for an unreported day. Emitting 0 for it would
        // plot a real zero over a hole, which is the one thing this card exists
        // to keep apart.
        ...(envelope.series !== undefined && {
            series: envelope.series.filter(reportsMetric).map((point) => ({
                date: point.date,
                value: finiteCell(point[DOWNLOADS_METRIC]),
            })),
        }),
        ...(envelope.dataCompleteThrough !== undefined && {
            dataCompleteThrough: envelope.dataCompleteThrough,
        }),
        ...(envelope.caveats.length > 0 && { caveats: envelope.caveats }),
    };
};

/**
 * Join degradation reasons into one sentence, dropping duplicates.
 *
 * Both endpoints resolve geography identically, so a province selection off the
 * CT/CK boundary degrades both with the very same wording: deduping renders it
 * verbatim, exactly as the single combined envelope used to. Reasons that
 * genuinely differ are platform-specific, and the API names the platform in
 * each of them.
 */
const joinReasons = (reasons: string[]): string =>
    [...new Set(reasons)].join(' ');

/** Envelope caveats from both platforms, deduped by text: geography resolves
 * identically on both endpoints, so a one-brand region selection carries the
 * identical sentence on each, and it must read once (see `joinReasons`). */
const dedupeCaveats = (caveats: EnvelopeCaveat[]): EnvelopeCaveat[] => [
    ...new Map(caveats.map((c) => [c.text, c])).values(),
];

/**
 * The period-over-period delta, from the two scalar compares.
 *
 * All-or-nothing on purpose: the API drops a platform's compare whenever that
 * platform's data does not fully cover the compare window, so summing the one
 * compare we did get would understate the prior period and render as fake
 * growth. No compare from both platforms, no delta.
 */
const downloadsTrend = (
    total: number,
    comparisonLabel: string,
    compares: (number | undefined)[]
): KpiTrend | undefined => {
    let previous = 0;
    for (const compare of compares) {
        if (compare === undefined) {
            return undefined;
        }
        previous += compare;
    }
    const delta = total - previous;
    return {
        delta,
        unit: 'count',
        direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
        comparisonLabel,
    };
};

/** The earlier of two ISO `YYYY-MM-DD` dates; they sort lexicographically, so no
 * parsing is needed. */
const earlierDate = (a: string, b: string): string => (a < b ? a : b);

/**
 * Said out loud whenever the chart's window is shorter than the period the
 * reader asked for.
 *
 * The bound itself is fine, silence about it is not. An axis that stopped short
 * without a word would put BOTH lines' last points at the same edge, which is
 * precisely the "the two stores went stale on the same day" reading this card
 * exists to disprove. The window keeps the most recent days rather than the
 * oldest, so the one thing the card is for, where each line ends, survives the
 * shortening.
 */
const shortWindowReason = (start: string, end: string): string =>
    `Only ${start} to ${end} is charted; the selected period is longer than ${MAX_ENUMERATED_DATES} days. The platform totals still cover all of it.`;

/**
 * Said out loud when the clipped window holds no day at all.
 *
 * Reachable in one click: the range picker allows a period starting after
 * today, and the axis is clipped at today, so `end` lands before `start` and
 * there is not one day to draw. The card has nothing honest to show in that
 * state, so it says the window instead of rendering bare axes under a legend
 * full of totals.
 */
const emptyWindowReason = (start: string, end: string): string =>
    `Nothing to chart: the selected period starts on ${start}, after ${end}, the last day this chart can cover.`;

/**
 * Said out loud when a platform served a total but no `series` axis at all,
 * under a request that asked for daily granularity.
 *
 * Ours, not the API's: a 200 with no series under `granularity=daily` is a
 * contract violation, so there is no API sentence to quote. The platform is
 * dropped from the chart rather than charted as a flat nothing, which is the
 * same treatment a platform that never answered gets.
 */
const noDailySeriesReason = (label: string): string =>
    `${label} downloads are not available by day for this period.`;

/** One platform's line, as the `downloads` card form carries it. */
type DownloadsChartSeries = Extract<
    KpiCardData,
    { form: 'downloads' }
>['series'][number];

/**
 * Card data for the `downloads` chart: one named daily series per platform that
 * answered, each with its own period total, over an axis spanning the selected
 * period.
 *
 * This is the form the two-endpoint split was made for, and it degrades the
 * opposite way to the value forms below. A total amputated of one platform is a
 * false figure and a donut missing a slice misstates every proportion, so those
 * go `unavailable`. Two lines are not one figure: losing Android costs the
 * reader the Android line and nothing else, so the card draws what it has and
 * says what it is missing. A platform that answered with a total but no daily
 * axis has no line either, so it degrades the same way rather than padding the
 * legend beside a plot it contributes nothing to.
 *
 * Losing BOTH is the same story one step further, so it gets the same answer:
 * the axis over the selected period, no line on it, and every missing platform
 * named under the legend. It is not `unavailable`, which replaces the chart
 * with its own text and makes a period no store has reached yet (the `Today`
 * capsule, where both stores lag by design) look like a different kind of card
 * from the one beside it. An empty plot under the period's own axis says
 * "nothing reported here" without a single figure on screen to be misread as
 * zero.
 *
 * The axis it declares in `range` is the axis a renderer must draw, and this is
 * the only place that decides it. A window the chart cannot draw whole is
 * shortened HERE, so `range` and the drawn axis can never disagree, and the
 * shortening rides along as a `warning` (see `shortWindowReason`) rather than
 * vanishing silently. A window with no day in it at all is not shortened but
 * refused (`emptyWindowReason`): there is no honest axis to declare.
 *
 * `data.missing` names the platforms with no line, deduped on `reason` so two
 * platforms failing identically read once.
 */
const composeDownloadsChart = (
    req: KpiCardDataRequest,
    entries: PlatformEntry[],
    today: string
): KpiCardState => {
    // Built in one pass so it stays in platform order.
    const missing: { cause?: DegradationCause; reason: string }[] = [];
    const series: DownloadsChartSeries[] = [];

    for (const entry of entries) {
        const { data } = entry;
        if (data.status !== 'served') {
            missing.push({
                reason: data.reason,
                ...(data.cause !== undefined && { cause: data.cause }),
            });
            continue;
        }
        // Served a total but no `series` axis at all, on a request that asked
        // for daily granularity. This platform has no line; the other one may
        // still have its own, so only this series is dropped, exactly as a
        // missing platform is. The alternative that used to run here was a
        // `ready` card with a numbered legend over an empty plot.
        if (data.series === undefined) {
            missing.push({
                reason: noDailySeriesReason(
                    DOWNLOADS_PLATFORM_LABELS[entry.platform]
                ),
            });
            continue;
        }
        series.push({
            label: DOWNLOADS_PLATFORM_LABELS[entry.platform],
            total: data.total,
            ...(data.dataCompleteThrough !== undefined && {
                dataCompleteThrough: data.dataCompleteThrough,
            }),
            // Verbatim: the envelope emits a point for every day it has data
            // for and none for the days it does not, and that distinction is
            // the whole signal. Nothing here pads, fills or truncates it.
            points: data.series,
        });
    }

    // The axis ends at the selected period's end or today, whichever comes
    // first, so it never runs into empty future. Each line then ends at its own
    // platform's freshness, short of the axis, which is how the card shows how
    // current each store report is.
    const end = earlierDate(req.period.end, today);
    // A period that has not started yet leaves nothing between the two bounds,
    // and an axis of no days is not an axis (see `emptyWindowReason`). This is
    // the one state with no chart to render, and the only one still
    // `unavailable`: an empty plot needs an axis, and there is none. Checked
    // before the clamp below, which has no shorter window to find here.
    if (end < req.period.start) {
        return {
            status: 'unavailable',
            reason: emptyWindowReason(req.period.start, end),
        };
    }
    // ...and it starts no earlier than the longest window the chart can draw.
    // The days that get dropped are the oldest ones, so both lines keep their
    // real endings.
    const start = clampWindowStart(req.period.start, end);
    const forwardedCaveats = entries
        .filter(isServedEntry)
        .flatMap((entry) => entry.data.caveats ?? []);
    const shortWindowWarning: EnvelopeCaveat[] =
        start !== req.period.start
            ? [
                  {
                      kind: 'warning',
                      code: 'chart_window_shortened',
                      text: shortWindowReason(start, end),
                  },
              ]
            : [];
    const caveats = dedupeCaveats([...forwardedCaveats, ...shortWindowWarning]);

    // Same platform failing on both envelopes reads once (e.g. a region
    // selection off the CT/CK boundary degrades both endpoints identically).
    const deduped = [
        ...new Map(missing.map((entry) => [entry.reason, entry])).values(),
    ];

    const data: KpiCardData = {
        form: 'downloads',
        range: { start, end },
        series,
        ...(deduped.length > 0 && { missing: deduped }),
    };

    return {
        status: 'ready',
        data,
        ...(caveats.length > 0 && { caveats }),
    };
};

/**
 * The by-app donut: the two envelopes' `brand` rows, summed per app. Rows
 * are merged on their `key`, keeping first-seen order, so the donut reads
 * Couche-Tard then Circle K exactly as the combined envelope emitted it.
 *
 * `undefined` when the axes do not agree on their set of keys. An app one
 * platform omits contributes an implicit 0 to the merged slice, so that slice
 * would be built from one store while the slice beside it is built from two,
 * and every proportion in the donut would be wrong with nothing on screen to
 * say so. The cell-level version of this is already refused (see
 * `reportsMetric`); a missing ROW says the same thing and is refused here, so
 * the donut is an error tile rather than a half-built one.
 */
const mergeBrandRows = (
    perPlatform: DownloadsBrandRow[][]
): DownloadsBrandRow[] | undefined => {
    const merged = new Map<string, DownloadsBrandRow>();
    for (const row of perPlatform.flat()) {
        const seen = merged.get(row.key);
        merged.set(
            row.key,
            seen ? { ...seen, value: seen.value + row.value } : { ...row }
        );
    }
    // Each platform's own key count matching the merged one is enough to prove
    // every axis carries every app: each platform's keys are a subset of the
    // merged set by construction, so equal sizes mean equal sets.
    const everyAxisCarriesEveryApp = perPlatform.every(
        (rows) => new Set(rows.map((row) => row.key)).size === merged.size
    );
    return everyAxisCarriesEveryApp ? [...merged.values()] : undefined;
};

/**
 * Merge both platforms into the card data for the requested form.
 *
 * Degradation is deliberately per-form, and that asymmetry is the whole point
 * of the split: a `number` amputated of one platform, shown as "the total",
 * would be a false figure, and a donut missing a slice misstates every
 * proportion. Both go `unavailable`, naming what is missing. The `downloads`
 * chart wants the opposite answer, so it is dispatched before the gate that
 * enforces this one (see `composeDownloadsChart`).
 *
 * `today` is the current calendar date as ISO `YYYY-MM-DD`, used only by the
 * `downloads` chart to clip its x-axis. It is a parameter rather than a clock
 * read so this module stays a pure function of its inputs.
 */
export const composeDownloadsCardData = (
    req: KpiCardDataRequest,
    platforms: Record<DownloadsPlatform, NormalizedPlatformDownloads>,
    today: string
): KpiCardState => {
    // Both endpoints serve `total` and nothing else, so a card pinned to any
    // other metric is a spec bug: an error tile, not a silent fallback. Ahead
    // of every form, the chart included: a card asking for a metric that does
    // not exist is broken whatever it meant to draw.
    if (req.metric !== undefined && req.metric !== DOWNLOADS_METRIC) {
        return { status: 'error' };
    }

    // Each platform kept next to its own response, so the by-platform donut
    // labels a total from the platform it actually came from rather than by
    // matching array positions.
    const entries: PlatformEntry[] = DOWNLOADS_PLATFORMS.map((platform) => ({
        platform,
        data: platforms[platform],
    }));

    // Above the missing-platform gate below on purpose: the chart is the one
    // form that keeps rendering with a platform missing, so it cannot go
    // through a gate that turns that into `unavailable`. Dispatching on a form
    // value that did not exist until this card did leaves every other form on
    // exactly the path it already took.
    if (req.form === 'downloads') {
        return composeDownloadsChart(req, entries, today);
    }

    const missing = entries
        .map((entry) => entry.data)
        .filter((data): data is MissingDownloads => data.status !== 'served');
    if (missing.length > 0) {
        // NEITHER endpoint produced a usable envelope: the API is unreachable
        // or drifted, so there is nothing honest to say about the data. The
        // generic error tile, exactly as before the split. One platform failing
        // is a different story: the other one answered, so the card names the
        // side it is missing instead (below).
        if (
            missing.length === entries.length &&
            missing.every((data) => data.status === 'failed')
        ) {
            return { status: 'error' };
        }
        // Named only when every missing entry agrees on it; a disagreement
        // has no single cause to report.
        const causes = new Set(missing.map((data) => data.cause));
        const cause = causes.size === 1 ? missing[0].cause : undefined;
        return {
            status: 'unavailable',
            reason: joinReasons(missing.map((data) => data.reason)),
            ...(cause !== undefined && { cause }),
        };
    }

    const served = entries.filter(isServedEntry);
    const caveats = dedupeCaveats(
        served.flatMap((entry) => entry.data.caveats ?? [])
    );
    const ready = (data: KpiCardData): KpiCardState => ({
        status: 'ready',
        data,
        ...(caveats.length > 0 && { caveats }),
    });

    const total = served.reduce((sum, entry) => sum + entry.data.total, 0);

    if (req.form === 'number') {
        const trend = req.compare
            ? downloadsTrend(
                  total,
                  req.compare.label,
                  served.map((entry) => entry.data.compare)
              )
            : undefined;
        return ready({
            form: 'number',
            value: total,
            ...(trend !== undefined && { trend }),
        });
    }

    if (req.form === 'pie') {
        // The by-platform donut is rebuilt from the two totals: it replaces the
        // `device` breakdown, which the API dropped as redundant once the two
        // platforms became separate endpoints.
        if (req.breakdownBy === 'device') {
            return ready({
                form: 'pie',
                slices: served.map((entry) => ({
                    label: DOWNLOADS_PLATFORM_LABELS[entry.platform],
                    value: entry.data.total,
                })),
                palette: req.palette,
            });
        }
        if (req.breakdownBy === 'brand') {
            const rows: DownloadsBrandRow[][] = [];
            for (const entry of served) {
                // An envelope that served a total but no `brand` axis is a
                // contract violation; an error tile, not a half-built donut.
                if (entry.data.brandRows === undefined) {
                    return { status: 'error' };
                }
                rows.push(entry.data.brandRows);
            }
            // Two axes that do not list the same apps cannot be summed into one
            // honest donut either (see `mergeBrandRows`).
            const brandRows = mergeBrandRows(rows);
            if (brandRows === undefined) {
                return { status: 'error' };
            }
            return ready({
                form: 'pie',
                slices: brandRows.map((row) => ({
                    label: row.label,
                    value: row.value,
                })),
                palette: req.palette,
            });
        }
        // A pie on an axis neither envelope carries.
        return { status: 'error' };
    }

    // A form this source does not produce (the spec/descriptor mismatch is
    // already warned about at card level).
    return { status: 'error' };
};
